import React, { useState, useEffect, useRef } from "react";
import { Toaster, toast } from "sonner";
import "./SectionStyles.css";
import "../UserForm.css";
import {
  submitPromptData,
  processPrompts,
  validateApiKey,
} from "../../services/prompts";
import { useNavigate, useLocation } from "react-router-dom";
import FileManager from "../common/FileManager";
import SelectLibraryComponet from "../common/SelectLibraryComponet";
import {
  getFolderContents,
  uploadFile,
} from "../../services/fileSystemService";
import { getMyLibraryFolder } from "../../services/fileSystemService";
import LoadingSpinner from "../common/LoadingSpinner";
import FolderSelector from "../common/FolderSelector";

const MyPrompt = () => {
  const [formData, setFormData] = useState({
    chapterPrompt: "",
    assessmentPrompt: "",
    tagsPrompt: "",
    trailerPrompt: "",
    summaryPrompt: "",
    folderPath: "",
    geminiKey: "",
    assemblyKey: "",
    folderBreadcrumb: "",
    folderName: "",
  });

  const [enabledSections, setEnabledSections] = useState({
    assessmentPrompt: false,
    tagsPrompt: false,
    trailerPrompt: false,
    summaryPrompt: false,
  });

  const [errors, setErrors] = useState({});
  const [isPreviewMode, setIsPreviewMode] = useState(false);

  // Modal States
  const [isProcessingModalOpen, setIsProcessingModalOpen] = useState(false);
  const [isLoadingFile, setIsLoadingFile] = useState(false);
  const [processedFile, setProcessedFile] = useState(null);

  const initialFeedbackData = {
    promptSearchName: "",
    selectedTag: "",
    accuracy: 0,
    usefulness: 0,
    relevancy: 0,
    frequency: 0,
    comment: "",
    poorPromptFields: {},
    source: "user_initiated",
  };

  const [modalStep, setModalStep] = useState(0); // 0: closed, 1: processing/file_view, 2: feedback, 3: save file
  const [feedbackData, setFeedbackData] = useState(initialFeedbackData);
  const [feedbackFormErrors, setFeedbackFormErrors] = useState({}); // For inline validation messages

  const [isUsedPrompt, setIsUsedPrompt] = useState(false);

  // Add state for uploaded files
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const [selectedFolderObj, setSelectedFolderObj] = useState(null);

  const tagOptions = [
    { value: "general", label: "General" },
    { value: "tech", label: "Technical" },
    { value: "creative", label: "Creative Writing" },
    { value: "marketing", label: "Marketing" },
    { value: "education", label: "Education" },
  ];

  const FEEDBACK_MODAL_STEP_KEY = "feedbackModalStep";
  const FEEDBACK_DATA_KEY = "feedbackFormData";

  const navigate = useNavigate();
  const location = useLocation();

  // Add supported formats constants
  const SUPPORTED_VIDEO_FORMATS = [
    "video/mp4",
    "video/x-msvideo", // .avi
    "video/quicktime", // .mov
    "video/x-matroska", // .mkv
  ];

  const SUPPORTED_AUDIO_FORMATS = [
    "audio/mpeg", // .mp3
    "audio/wav",
    "audio/aac",
  ];

  const SUPPORTED_DOCUMENT_FORMATS = [
    "application/pdf",
    "application/msword", // .doc
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
  ];

  // Add at the top of the component:
  const [feedbackSource, setFeedbackSource] = useState(null);

  // API Key validation states
  const [geminiKeyValid, setGeminiKeyValid] = useState(false);
  const [assemblyKeyValid, setAssemblyKeyValid] = useState(false);
  const [isValidatingGemini, setIsValidatingGemini] = useState(false);
  const [isValidatingAssembly, setIsValidatingAssembly] = useState(false);

  // Effect to load persisted feedback state on mount
  useEffect(() => {
    const persistedStep = localStorage.getItem(FEEDBACK_MODAL_STEP_KEY);
    const persistedData = localStorage.getItem(FEEDBACK_DATA_KEY);

    if (persistedStep && parseInt(persistedStep, 10) === 2 && persistedData) {
      setModalStep(2);
      setFeedbackData(JSON.parse(persistedData));
      setIsProcessingModalOpen(true); // Open the modal if we are restoring to step 2
      // We might need to restore `processedFile` too if it's relevant for step 2,
      // but currently, step 2 only depends on feedbackData.
      // If `processedFile` was needed, it should also be persisted.
      // For now, assuming `processedFile` is mainly for step 1.
    }
  }, []);

  // Auto-select user's personal library folder if available
  useEffect(() => {
    (async () => {
      try {
        const myFolder = await getMyLibraryFolder();
        if (myFolder && myFolder._id) {
          setSelectedFolderObj(myFolder);
          setFormData((prev) => ({
            ...prev,
            folderPath: myFolder._id,
            folderBreadcrumb: myFolder.path || myFolder.name,
          }));
        }
      } catch (e) {
        // silent fail
      }
    })();
  }, []);

  // Effect to save feedback state to localStorage when on step 2
  useEffect(() => {
    if (modalStep === 2 && isProcessingModalOpen) {
      localStorage.setItem(FEEDBACK_MODAL_STEP_KEY, modalStep.toString());
      localStorage.setItem(FEEDBACK_DATA_KEY, JSON.stringify(feedbackData));
    } else {
      localStorage.removeItem(FEEDBACK_MODAL_STEP_KEY);
      localStorage.removeItem(FEEDBACK_DATA_KEY);
    }
  }, [modalStep, feedbackData, isProcessingModalOpen]);

  // Effect to toggle body scroll based on modal open state
  useEffect(() => {
    if (isProcessingModalOpen) {
      document.body.classList.add("body-no-scroll");
    } else {
      document.body.classList.remove("body-no-scroll");
    }
    // Cleanup function to remove the class if the component unmounts while modal is open
    return () => {
      document.body.classList.remove("body-no-scroll");
    };
  }, [isProcessingModalOpen]);

  // Prefill from Use button in PromptsView (now via navigation state)
  useEffect(() => {
    if (location.state && location.state.prefillPrompt) {
      const promptTexts = location.state.prefillPrompt;
      const newFormData = { ...formData };
      const newEnabledSections = { ...enabledSections };
      if (promptTexts.chapter) newFormData.chapterPrompt = promptTexts.chapter;
      if (promptTexts.assessment) {
        newFormData.assessmentPrompt = promptTexts.assessment;
        newEnabledSections.assessmentPrompt = true;
      }
      if (promptTexts.tags) {
        newFormData.tagsPrompt = promptTexts.tags;
        newEnabledSections.tagsPrompt = true;
      }
      if (promptTexts.trailer) {
        newFormData.trailerPrompt = promptTexts.trailer;
        newEnabledSections.trailerPrompt = true;
      }
      if (promptTexts.summary) {
        newFormData.summaryPrompt = promptTexts.summary;
        newEnabledSections.summaryPrompt = true;
      }
      setFormData(newFormData);
      setEnabledSections(newEnabledSections);
      setIsPreviewMode(true); // Immediately show preview
      setIsUsedPrompt(true); // Mark as used prompt
      // Optionally clear the state so it doesn't persist on back/forward
      navigate(location.pathname + location.search, {
        replace: true,
        state: {},
      });
    }
  }, [location]);

  // Effect to reset validation status when input values change
  useEffect(() => {
    setGeminiKeyValid(false);
  }, [formData.geminiKey]);

  useEffect(() => {
    setAssemblyKeyValid(false);
  }, [formData.assemblyKey]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field] && !isPreviewMode) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }

    // Reset validation status when API key fields change
    if (field === "geminiKey") {
      setGeminiKeyValid(false);
    } else if (field === "assemblyKey") {
      setAssemblyKeyValid(false);
    }
  };

  const handleSectionToggle = (section) => {
    if (isPreviewMode) return;
    setEnabledSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));

    if (enabledSections[section]) {
      setFormData((prev) => ({
        ...prev,
        [section]: "",
      }));
      setErrors((prev) => ({
        ...prev,
        [section]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.chapterPrompt.trim()) {
      newErrors.chapterPrompt = "Chapter Prompt is required";
    }

    Object.keys(enabledSections).forEach((section) => {
      if (enabledSections[section] && !formData[section].trim()) {
        const fieldName = section
          .replace(/([A-Z])/g, " $1")
          .replace(/^./, (str) => str.toUpperCase());
        newErrors[section] = `${fieldName} is required when enabled`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFormSubmit = () => {
    if (validateForm()) {
      handleFinalSubmit();
      setIsPreviewMode(true);
    } else {
      toast.error("Please fix the errors before submitting", {
        duration: 4000,
        position: "top-right",
        style: {
          background: "#ef4444",
          color: "#fff",
        },
      });
    }
  };

  const handleEdit = () => {
    setIsPreviewMode(false);
    setIsUsedPrompt(false); // Now it's a new prompt
  };

  const handleFinalSubmit = () => {
    const submissionData = {
      promptTexts: {
        chapter: formData.chapterPrompt || null,
        assessment:
          enabledSections.assessmentPrompt && formData.assessmentPrompt
            ? formData.assessmentPrompt
            : null,
        tags:
          enabledSections.tagsPrompt && formData.tagsPrompt
            ? formData.tagsPrompt
            : null,
        trailer:
          enabledSections.trailerPrompt && formData.trailerPrompt
            ? formData.trailerPrompt
            : null,
        summary:
          enabledSections.summaryPrompt && formData.summaryPrompt
            ? formData.summaryPrompt
            : null,
      },
      source: "user_initiated",
    };

    console.log("Final Submission Data:", submissionData);
    toast.success("Prompt submitted successfully!", {
      duration: 4000,
      position: "top-right",
      style: {
        background: "#10b981",
        color: "#fff",
      },
      iconTheme: {
        primary: "#fff",
        secondary: "#10b981",
      },
    });

    // Don't reset the enabled sections after submission
    // setEnabledSections({
    //   assessmentPrompt: false,
    //   tagsPrompt: false,
    //   trailerPrompt: false,
    //   summaryPrompt: false,
    // });

    setErrors({});
    setIsPreviewMode(false);
    // Reset modal states if clear is pressed while modal might be open or in a specific state
    setIsProcessingModalOpen(false);
    setIsLoadingFile(false);
    setProcessedFile(null);
    setModalStep(0);
    setFeedbackData(initialFeedbackData);
    setFeedbackFormErrors({});
  };

  const handleClear = () => {
    setFormData({
      chapterPrompt: "",
      assessmentPrompt: "",
      tagsPrompt: "",
      trailerPrompt: "",
      summaryPrompt: "",
      folderPath: "",
      geminiKey: "",
      assemblyKey: "",
      folderBreadcrumb: "",
      folderName: "",
    });
    setEnabledSections({
      assessmentPrompt: false,
      tagsPrompt: false,
      trailerPrompt: false,
      summaryPrompt: false,
    });
    setErrors({});
    setIsPreviewMode(false);
    // Reset modal states if clear is pressed while modal might be open or in a specific state
    setIsProcessingModalOpen(false);
    setIsLoadingFile(false);
    setProcessedFile(null);
    setModalStep(0);
    setFeedbackData(initialFeedbackData);
    setFeedbackFormErrors({});
    toast.success("Form cleared successfully!", {
      duration: 2000,
      position: "top-right",
    });
  };

  const handleProcessFiles = async () => {
    try {
      setIsProcessingModalOpen(true);
      setIsLoadingFile(true);
      setModalStep(1);

      const promptsData = {
        chapterPrompt: formData.chapterPrompt,
        assessmentPrompt: {
          enabled: enabledSections.assessmentPrompt,
          text: formData.assessmentPrompt,
        },
        summaryPrompt: {
          enabled: enabledSections.summaryPrompt,
          text: formData.summaryPrompt,
        },
        tagsPrompt: {
          enabled: enabledSections.tagsPrompt,
          text: formData.tagsPrompt,
        },
        trailerPrompt: {
          enabled: enabledSections.trailerPrompt,
          text: formData.trailerPrompt,
        },
      };

      // Get file if one was uploaded
      const file = uploadedFiles[0] || null;

      // Log the complete payload
      console.log("Processing Files Payload:", {
        file,
        geminiKey: formData.geminiKey,
        assemblyKey: formData.assemblyKey,
        promptsData,
        selectedFolder: selectedFolderObj,
        folderName: formData.folderName,
        folderPath: formData.folderPath,
        folderBreadcrumb: formData.folderBreadcrumb,
      });

      const response = await processPrompts(
        file,
        formData.geminiKey,
        formData.assemblyKey,
        promptsData,
        selectedFolderObj ? selectedFolderObj._id : null,
        formData.folderName || null
      );

      // Log the API response
      console.log("API Response:", response);

      if (response.success && response.message) {
        setProcessedFile({
          ...response,
          name:
            response.message.finalFileName ||
            response.message.originalName ||
            "No file",
          type: response.message.fileType || "unknown",
          message: response.message,
        });

        toast.success("Processing completed successfully!");
      } else {
        throw new Error("Processing failed");
      }

      setIsLoadingFile(false);
    } catch (error) {
      console.error("Processing error:", error);
      toast.error(error.message || "Processing failed");
      setIsProcessingModalOpen(false);
      setIsLoadingFile(false);
      setModalStep(0);
    }
  };

  const handleProcessFilesClick = () => {
    // Validate required fields
    if (!formData.geminiKey) {
      toast.error("Please enter your Gemini API Key");
      return;
    }

    // Check if any uploaded files are audio/video
    const hasAudioVideo = uploadedFiles.some(
      (file) => file.type.startsWith("audio/") || file.type.startsWith("video/")
    );

    // Only require Assembly AI key if there are audio/video files
    if (hasAudioVideo && !formData.assemblyKey) {
      toast.error(
        "Assembly AI Key is required for processing audio/video files"
      );
      return;
    }

    // Use selectedFolderObj for folder check
    console.log("selectedFolder", selectedFolderObj);
    if (!selectedFolderObj) {
      toast.error("Please select a folder");
      return;
    }
    if (!formData.folderName) {
      toast.error("Please enter a folder name");
      return;
    }

    // If all validations pass, proceed with processing
    handleProcessFiles();
  };

  // Folder selection handler for processing files
  const handleProcessingFolderSelect = (folder) => {
    console.log("Selected folder:", folder);
  };

  // API Key validation handlers
  const handleValidateGeminiKey = async () => {
    if (!formData.geminiKey.trim()) return;

    setIsValidatingGemini(true);
    try {
      const response = await validateApiKey(formData.geminiKey, "gemini");
      if (response.success) {
        setGeminiKeyValid(true);
        toast.success("Gemini API key is valid!");
      } else {
        setGeminiKeyValid(false);
        toast.error("Invalid Gemini API key");
      }
    } catch (error) {
      setGeminiKeyValid(false);
      toast.error(error.message || "Error validating Gemini API key");
    } finally {
      setIsValidatingGemini(false);
    }
  };

  const handleValidateAssemblyKey = async () => {
    if (!formData.assemblyKey.trim()) return;

    setIsValidatingAssembly(true);
    try {
      const response = await validateApiKey(formData.assemblyKey, "assembly");
      if (response.success) {
        setAssemblyKeyValid(true);
        toast.success("AssemblyAI API key is valid!");
      } else {
        setAssemblyKeyValid(false);
        toast.error("Invalid AssemblyAI API key");
      }
    } catch (error) {
      setAssemblyKeyValid(false);
      toast.error(error.message || "Error validating AssemblyAI API key");
    } finally {
      setIsValidatingAssembly(false);
    }
  };

  // Modal Handlers
  const handleCloseModal = () => {
    // Prevent closing if in the middle of a non-cancellable step
    if (modalStep === 1 && !isLoadingFile && processedFile) return;
    // if (modalStep === 2) return; // We removed the X button for step 2

    setIsProcessingModalOpen(false);
    setIsLoadingFile(false);
    setProcessedFile(null);
    setModalStep(0); // This will trigger the useEffect to clear localStorage
    setFeedbackData(initialFeedbackData);
    setFeedbackFormErrors({}); // Clear inline errors
  };

  const handleRejectFile = () => {
    setFeedbackSource("rejected");
    setModalStep(2); // Move to feedback step
    setFeedbackFormErrors({});
  };

  const handleAddFile = () => {
    if (!processedFile) {
      toast.error("No processed content available to save.");
      return;
    }
    setFeedbackSource("user_initiated");
    setModalStep(2); // Move to Feedback step
    setFeedbackFormErrors({});
  };

  const handleFeedbackInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let currentFeedbackFormErrors = { ...feedbackFormErrors };

    if (name === "poorPromptFields") {
      setFeedbackData((prev) => ({
        ...prev,
        poorPromptFields: {
          ...prev.poorPromptFields,
          [value]: checked,
        },
      }));
    } else if (
      ["accuracy", "usefulness", "relevancy", "frequency"].includes(name)
    ) {
      const floatValue = parseFloat(value);
      let newValueForState = 0; // Default if input is cleared or invalid text
      if (!isNaN(floatValue)) {
        newValueForState = floatValue;
        if (floatValue > 5) {
          currentFeedbackFormErrors[name] = "Max rating is 5.";
        } else {
          currentFeedbackFormErrors[name] = ""; // Clear error if valid
        }
      } else if (value.trim() === "") {
        currentFeedbackFormErrors[name] = ""; // Clear error if field is emptied
      } else {
        // Invalid non-numeric input, but not > 5, so don't set specific "Max rating" error yet.
        // The main validation on submit will catch it as not being between 1-5.
        // Or, we could set a generic "Invalid number" error here.
        currentFeedbackFormErrors[name] = "Invalid number.";
      }

      setFeedbackData((prev) => ({
        ...prev,
        [name]: newValueForState,
      }));
    } else {
      setFeedbackData((prev) => ({
        ...prev,
        [name]: value,
      }));
      // Clear potential non-rating field errors if any were to be added
      if (currentFeedbackFormErrors[name]) {
        currentFeedbackFormErrors[name] = "";
      }
    }
    setFeedbackFormErrors(currentFeedbackFormErrors);
  };

  const handleSubmitFeedback = async () => {
    const {
      promptSearchName,
      selectedTag,
      accuracy,
      usefulness,
      relevancy,
      frequency,
      comment,
      poorPromptFields,
      source,
    } = feedbackData;
    const newErrors = {};

    if (!promptSearchName.trim())
      newErrors.promptSearchName = "Prompt search name is required.";
    if (!selectedTag) newErrors.selectedTag = "Tag is required.";

    const ratings = { accuracy, usefulness, relevancy, frequency };
    for (const key in ratings) {
      if (ratings[key] === 0) {
        newErrors[key] = `${
          key.charAt(0).toUpperCase() + key.slice(1)
        } rating is required.`;
      } else if (ratings[key] < 1 || ratings[key] > 5) {
        newErrors[key] = `${
          key.charAt(0).toUpperCase() + key.slice(1)
        } rating must be between 1 and 5.`;
      }
    }

    if (accuracy > 0 && accuracy <= 3) {
      if (!comment.trim())
        newErrors.comment = "Comment is required when accuracy is 3 or less.";
      const selectedPoorFields = Object.values(poorPromptFields).some(
        (isSelected) => isSelected
      );
      if (!selectedPoorFields)
        newErrors.poorPromptFields =
          "At least one poor prompt field must be selected when accuracy is 3 or less.";
    }

    if (Object.keys(newErrors).length > 0) {
      let errorMessages = Object.values(newErrors).join("\n");
      toast.error(`Please fix the following errors:\n${errorMessages}`, {
        duration: 5000,
        position: "top-right",
        style: { whiteSpace: "pre-line" },
      });
      return;
    }

    // Construct payload for the backend
    const feedbackPayload = {
      ...feedbackData,
      originalProcessedFileName: processedFile ? processedFile.name : null,
      promptTexts: {
        chapter: formData.chapterPrompt || null,
        assessment:
          enabledSections.assessmentPrompt && formData.assessmentPrompt
            ? formData.assessmentPrompt
            : null,
        tags:
          enabledSections.tagsPrompt && formData.tagsPrompt
            ? formData.tagsPrompt
            : null,
        trailer:
          enabledSections.trailerPrompt && formData.trailerPrompt
            ? formData.trailerPrompt
            : null,
        summary:
          enabledSections.summaryPrompt && formData.summaryPrompt
            ? formData.summaryPrompt
            : null,
      },
      source: feedbackSource || "user_initiated",
      ...(processedFile &&
      processedFile.message &&
      processedFile.message.uploadedFileId
        ? { uploadFileId: processedFile.message.uploadedFileId }
        : {}),
      ...(processedFile &&
      processedFile.message &&
      processedFile.message.selectedFolderId
        ? { contentFolderId: processedFile.message.selectedFolderId }
        : {}),
    };

    try {
      const responseData = await submitPromptData(feedbackPayload);
      toast.success(
        responseData.message || "Feedback submitted successfully!",
        { position: "top-right" }
      );
      setIsProcessingModalOpen(false);
      setModalStep(0);
      setFeedbackData(initialFeedbackData);
      setFeedbackFormErrors({});
      // Redirect to All Prompts page
      navigate("/?tab=all-prompts");
    } catch (error) {
      console.error("Error submitting feedback to backend:", error);
      toast.error(
        error.message || "Failed to submit feedback. Please try again.",
        {
          position: "top-right",
          duration: 4000,
        }
      );
    }
    setFeedbackSource(null); // Reset after submit
  };

  // Update isValidFileType function
  const isValidFileType = (file) => {
    // Check if it's a video file
    if (SUPPORTED_VIDEO_FORMATS.includes(file.type)) {
      return true;
    }

    // Check if it's an audio file
    if (SUPPORTED_AUDIO_FORMATS.includes(file.type)) {
      return true;
    }

    // Check if it's a document
    if (SUPPORTED_DOCUMENT_FORMATS.includes(file.type)) {
      return true;
    }

    // Check file extension for cases where MIME type might not be recognized
    const extension = file.name.toLowerCase().split(".").pop();
    const validExtensions = [
      "mp4",
      "avi",
      "mov",
      "mkv",
      "mp3",
      "wav",
      "aac",
      "pdf",
      "doc",
      "docx",
    ];

    return validExtensions.includes(extension);
  };

  // Update handleFileSelect to handle single file
  const handleFileSelect = (event) => {
    const file = event.target.files[0]; // Get only the first file
    if (!file) return;

    // Validate file type
    if (!isValidFileType(file)) {
      toast.error(`File type not supported: ${file.name}`, {
        duration: 3000,
        position: "top-right",
      });
      event.target.value = "";
      return;
    }

    setUploadedFiles([file]); // Replace any existing file with the new one
    console.log("Currently uploaded file:", file);
    event.target.value = "";
  };

  // Update getFileIcon to handle more formats
  const getFileIcon = (fileType) => {
    // Check by MIME type first
    if (SUPPORTED_VIDEO_FORMATS.includes(fileType)) return "🎥";
    if (SUPPORTED_AUDIO_FORMATS.includes(fileType)) return "🎵";
    if (fileType.startsWith("image/")) return "🖼️";
    if (fileType === "application/pdf") return "📄";
    if (
      fileType.includes("document") ||
      fileType.includes("msword") ||
      fileType.includes("officedocument")
    )
      return "📝";

    // Fallback to extension check
    const extension = fileType.split("/").pop().toLowerCase();
    if (
      ["mp4", "webm", "ogg", "mov", "avi", "mkv", "wmv", "3gp", "flv"].includes(
        extension
      )
    )
      return "🎥";
    if (["mp3", "wav", "aac", "midi", "mid", "m4a"].includes(extension))
      return "🎵";

    return "📁";
  };

  // Handle file removal
  const handleRemoveFile = (indexToRemove) => {
    setUploadedFiles((prev) =>
      prev.filter((_, index) => index !== indexToRemove)
    );
  };

  const promptsToDisplay = [
    {
      key: "chapterPrompt",
      label: "Chapter Prompt",
      value: formData.chapterPrompt,
      alwaysShow: true,
    },
    {
      key: "assessmentPrompt",
      label: "Assessment Prompt",
      value: formData.assessmentPrompt,
      enabled: enabledSections.assessmentPrompt,
    },
    {
      key: "tagsPrompt",
      label: "Tags Prompt",
      value: formData.tagsPrompt,
      enabled: enabledSections.tagsPrompt,
    },
    {
      key: "trailerPrompt",
      label: "Trailer Prompt",
      value: formData.trailerPrompt,
      enabled: enabledSections.trailerPrompt,
    },
    {
      key: "summaryPrompt",
      label: "Summary Prompt",
      value: formData.summaryPrompt,
      enabled: enabledSections.summaryPrompt,
    },
  ];

  const [showFolderSelector, setShowFolderSelector] = useState(false);

  // Folder selection handler for saving files
  const handleSaveFolderSelect = (data) => {
    setFormData((prev) => ({
      ...prev,
      folderPath: data.folder._id,
      folderBreadcrumb: data.path,
    }));
    setSelectedFolderObj(data.folder); // Store the full folder object
    toast.success(`Selected folder: ${data.folder.name}`);
  };

  const handleCancel = () => {
    // Reset form data
    setFormData({
      geminiKey: "",
      folderPath: "",
      folderBreadcrumb: "",
      folderName: "",
    });
    // Navigate back or handle cancel action
    navigate("/prompts");
  };

  const handleSubmit = () => {
    // Validate required fields
    if (!formData.geminiKey?.trim()) {
      toast.error("Please enter your Gemini API Key");
      return;
    }

    if (!formData.folderPath) {
      toast.error("Please select a folder for files");
      return;
    }

    if (!formData.folderName?.trim()) {
      toast.error("Please enter a folder name");
      return;
    }

    // If all validations pass, proceed with submission
    handleProcessFilesClick();
  };

  return (
    <div className="my-prompt-container">
      <style>
        {`
          .upload-file-group {
            margin-bottom: ${uploadedFiles.length > 0 ? "20px" : "10px"};
          }

          .uploaded-files-container {
            display: flex;
            flex-direction: row;
            gap: 16px;
            margin-top: ${uploadedFiles.length > 0 ? "15px" : "0"};
            overflow-x: auto;
            padding: ${uploadedFiles.length > 0 ? "10px 5px" : "14px"};
            min-height: ${uploadedFiles.length > 0 ? "120px" : "0"};
            scrollbar-width: thin;
            scrollbar-color: #cbd5e0 #f7fafc;
          }

          .uploaded-files-container::-webkit-scrollbar {
            height: ${uploadedFiles.length > 0 ? "6px" : "0"};
          }

          .uploaded-files-container::-webkit-scrollbar-track {
            background: #f7fafc;
            border-radius: 10px;
          }

          .uploaded-files-container::-webkit-scrollbar-thumb {
            background-color: #cbd5e0;
            border-radius: 10px;
          }

          .file-card {
            position: relative;
            padding: 16px;
            border-radius: 10px;
            background: linear-gradient(145deg, #ffffff, #f5f7fa);
            width: 200px;
            min-width: 200px;
            box-shadow: 5px 5px 10px #e2e8f0, -5px -5px 10px #ffffff;
            transition: all 0.3s ease;
            display: flex;
            flex-direction: column;
            align-items: center;
            border: 1px solid #edf2f7;
          }

          .file-card:hover {
            transform: translateY(-3px);
            box-shadow: 6px 6px 12px #d1d9e6, -6px -6px 12px #ffffff;
          }

          .file-card .remove-button {
            position: absolute;
            top: -6px;
            right: -6px;
            background: #ff4444;
            color: white;
            border: 2px solid #fff;
            border-radius: 50%;
            width: 22px;
            height: 22px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 14px;
            font-weight: bold;
            box-shadow: 2px 2px 5px rgba(0,0,0,0.2);
            transition: all 0.2s ease;
            z-index: 2;
          }

          .file-card .remove-button:hover {
            background: #ff0000;
            transform: scale(1.1);
          }

          .file-card .file-icon {
            font-size: 36px;
            margin-bottom: 12px;
            background: #f7fafc;
            width: 60px;
            height: 60px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            border: 2px solid #edf2f7;
          }

          .file-card .file-name {
            font-size: 14px;
            color: #2d3748;
            text-align: center;
            margin: 8px 0;
            font-weight: 500;
            word-break: break-word;
            max-width: 180px;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }

          .file-card .file-size {
            font-size: 12px;
            color: #718096;
            background: #edf2f7;
            padding: 4px 12px;
            border-radius: 15px;
            margin-top: auto;
          }

          .files-counter {
            margin: 10px 0;
            color: #4a5568;
            font-size: 14px;
            font-weight: 500;
          }

          .empty-state {
            color: #718096;
            width: 100%;
            text-align: center;
            padding: 25px;
            border: 2px dashed #e2e8f0;
            border-radius: 12px;
            background: #f8fafc;
            font-size: 14px;
          }
        `}
      </style>

      <Toaster position="top-right" richColors />
      <div
        className="section-content"
        style={{
          maxWidth: "1200px",
          margin: "2rem auto",
          background: "white",
          borderRadius: "16px",
          padding: "2rem",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
          border: "1px solid #e5e7eb",
        }}
      >
        {!isPreviewMode ? (
          <div className="prompt-form">
            {/* Chapter Prompt - Always Required */}
            <div className="form-group">
              <label htmlFor="chapterPrompt" className="required-label">
                Chapter Prompt *
              </label>
              <textarea
                id="chapterPrompt"
                value={formData.chapterPrompt}
                onChange={(e) =>
                  handleInputChange("chapterPrompt", e.target.value)
                }
                placeholder="Enter your chapter prompt here... This field is required."
                className={`form-textarea ${
                  errors.chapterPrompt ? "error" : ""
                }`}
              />
              {errors.chapterPrompt && (
                <div className="error-message">{errors.chapterPrompt}</div>
              )}
              <div className="character-count">
                {formData.chapterPrompt.length} characters
              </div>
            </div>

            {/* Assessment Prompt */}
            <div className="form-group">
              <div className="standalone-checkbox-wrapper">
                <input
                  type="checkbox"
                  id="enable-assessmentPrompt"
                  checked={enabledSections.assessmentPrompt}
                  onChange={() => handleSectionToggle("assessmentPrompt")}
                  className="standalone-form-checkbox my-custom-enable-checkbox"
                />
              </div>
              <label htmlFor="assessmentPrompt">Assessment Prompt</label>
              <textarea
                id="assessmentPrompt"
                value={formData.assessmentPrompt}
                onChange={(e) =>
                  handleInputChange("assessmentPrompt", e.target.value)
                }
                placeholder="Enter your assessment prompt here..."
                className={`form-textarea ${
                  errors.assessmentPrompt ? "error" : ""
                }`}
                disabled={!enabledSections.assessmentPrompt}
              />
              {enabledSections.assessmentPrompt && errors.assessmentPrompt && (
                <div className="error-message">{errors.assessmentPrompt}</div>
              )}
              <div className="character-count">
                {formData.assessmentPrompt.length} characters
              </div>
            </div>

            {/* Tags Prompt */}
            <div className="form-group">
              <div className="standalone-checkbox-wrapper">
                <input
                  type="checkbox"
                  id="enable-tagsPrompt"
                  checked={enabledSections.tagsPrompt}
                  onChange={() => handleSectionToggle("tagsPrompt")}
                  className="standalone-form-checkbox my-custom-enable-checkbox"
                />
              </div>
              <label htmlFor="tagsPrompt">Tags Prompt</label>
              <textarea
                id="tagsPrompt"
                value={formData.tagsPrompt}
                onChange={(e) =>
                  handleInputChange("tagsPrompt", e.target.value)
                }
                placeholder="Enter your tags prompt here..."
                className={`form-textarea ${errors.tagsPrompt ? "error" : ""}`}
                disabled={!enabledSections.tagsPrompt}
              />
              {enabledSections.tagsPrompt && errors.tagsPrompt && (
                <div className="error-message">{errors.tagsPrompt}</div>
              )}
              <div className="character-count">
                {formData.tagsPrompt.length} characters
              </div>
            </div>

            {/* Trailer Prompt */}
            <div className="form-group">
              <div className="standalone-checkbox-wrapper">
                <input
                  type="checkbox"
                  id="enable-trailerPrompt"
                  checked={enabledSections.trailerPrompt}
                  onChange={() => handleSectionToggle("trailerPrompt")}
                  className="standalone-form-checkbox my-custom-enable-checkbox"
                />
              </div>
              <label htmlFor="trailerPrompt">Trailer Prompt</label>
              <textarea
                id="trailerPrompt"
                value={formData.trailerPrompt}
                onChange={(e) =>
                  handleInputChange("trailerPrompt", e.target.value)
                }
                placeholder="Enter your trailer prompt here..."
                className={`form-textarea ${
                  errors.trailerPrompt ? "error" : ""
                }`}
                disabled={!enabledSections.trailerPrompt}
              />
              {enabledSections.trailerPrompt && errors.trailerPrompt && (
                <div className="error-message">{errors.trailerPrompt}</div>
              )}
              <div className="character-count">
                {formData.trailerPrompt.length} characters
              </div>
            </div>

            {/* Summary Prompt */}
            <div className="form-group">
              <div className="standalone-checkbox-wrapper">
                <input
                  type="checkbox"
                  id="enable-summaryPrompt"
                  checked={enabledSections.summaryPrompt}
                  onChange={() => handleSectionToggle("summaryPrompt")}
                  className="standalone-form-checkbox my-custom-enable-checkbox"
                />
              </div>
              <label htmlFor="summaryPrompt">Summary Prompt</label>
              <textarea
                id="summaryPrompt"
                value={formData.summaryPrompt}
                onChange={(e) =>
                  handleInputChange("summaryPrompt", e.target.value)
                }
                placeholder="Enter your summary prompt here..."
                className={`form-textarea ${
                  errors.summaryPrompt ? "error" : ""
                }`}
                disabled={!enabledSections.summaryPrompt}
              />
              {enabledSections.summaryPrompt && errors.summaryPrompt && (
                <div className="error-message">{errors.summaryPrompt}</div>
              )}
              <div className="character-count">
                {formData.summaryPrompt.length} characters
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                onClick={handleClear}
                className="btn btn-secondary"
              >
                Clear All
              </button>
              <button
                type="button"
                onClick={handleFormSubmit}
                className="btn btn-primary"
                disabled={!formData.chapterPrompt.trim()}
              >
                Submit Prompt
              </button>
            </div>
          </div>
        ) : (
          <div className="prompt-preview-area">
            <div className="preview-header-controls">
              <h3>Review Your Submitted Prompts</h3>
              <button
                type="button"
                onClick={handleEdit}
                className="btn btn-secondary btn-sm edit-prompt-btn"
              >
                Edit Prompts
              </button>
            </div>
            <div className="preview-cards-container">
              {promptsToDisplay.map(
                (prompt) =>
                  (prompt.alwaysShow ||
                    (prompt.enabled &&
                      prompt.value &&
                      prompt.value.trim() !== "")) && (
                    <div key={prompt.key} className="preview-card-item">
                      <h4>{prompt.label}</h4>
                      <p>{prompt.value}</p>
                    </div>
                  )
              )}
            </div>
          </div>
        )}

        {/* File Processing Section - Conditionally Rendered */}
        {isPreviewMode && modalStep === 0 && !isProcessingModalOpen && (
          <div className="file-processing-section">
            <h4 className="section-subtitle">File Processing Configuration</h4>

            <div style={{ display: "flex", gap: "2rem", marginBottom: "2rem" }}>
              {/* Upload File Section */}
              <div className="form-group" style={{ flex: 1 }}>
                <label>Upload File:</label>
                <input
                  type="file"
                  id="fileUpload"
                  onChange={handleFileSelect}
                  style={{ display: "none" }}
                  accept=".pdf,.doc,.docx,audio/mp3,audio/wav,audio/aac,.mp3,.wav,.aac,video/mp4,video/avi,video/quicktime,video/x-matroska,.mp4,.avi,.mov,.mkv"
                />
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => document.getElementById("fileUpload").click()}
                >
                  Choose File
                </button>

                <div className="uploaded-files-container">
                  {uploadedFiles.map((file, index) => (
                    <div key={`${file.name}-${index}`} className="file-card">
                      <button
                        className="remove-button"
                        onClick={() => handleRemoveFile(index)}
                        title="Remove file"
                      >
                        ×
                      </button>
                      <div className="file-icon">{getFileIcon(file.type)}</div>
                      <div className="file-name">{file.name}</div>
                      <div className="file-size">
                      {(((file?.size ?? file?.fileMeta?.size) / (1024 * 1024)) || 0).toFixed(2)} MB
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Select Folder Section */}
              <div className="form-group" style={{ flex: 1 }}>
                <label>Select Folder to store the file:</label>
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => setShowFolderSelector(true)}
                >
                  Select folder for upload Files
                </button>

                <div className="uploaded-files-container">
                  {formData.folderBreadcrumb && (
                    <div className="file-card">
                      <button
                        className="remove-button"
                        onClick={() =>
                          setFormData((prev) => ({
                            ...prev,
                            folderPath: "",
                            folderBreadcrumb: "",
                          }))
                        }
                        title="Remove folder"
                      >
                        ×
                      </button>
                      <div className="file-icon">📁</div>
                      <div className="file-name">Selected Folder</div>
                      <div className="file-size">
                        {formData.folderBreadcrumb}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Folder Name Field */}
            <div className="form-group" style={{ marginBottom: "1.5rem" }}>
              <label
                style={{
                  fontWeight: 600,
                  fontSize: "1.05rem",
                  color: "#374151",
                  marginBottom: 4,
                  display: "block",
                }}
              >
                Folder Name for Generated files{" "}
              </label>
              <input
                type="text"
                className="form-control"
                value={formData.folderName || ""}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    folderName: e.target.value,
                  }))
                }
                placeholder="Enter folder name"
                style={{
                  width: "40%",
                  padding: "12px 16px",
                  border: "1.5px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "1.08rem",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                  transition: "border-color 0.2s, box-shadow 0.2s",
                  outline: "none",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#2563eb")}
                onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
              />
            </div>

            {/* API Keys Section */}
            <div
              className="form-row"
              style={{ display: "flex", gap: "1rem", marginBottom: "1rem" }}
            >
              <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                <label htmlFor="geminiKey">Insert Gemini Key:</label>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <input
                    type="text"
                    id="geminiKey"
                    value={formData.geminiKey}
                    onChange={(e) =>
                      handleInputChange("geminiKey", e.target.value)
                    }
                    placeholder="Enter your Gemini API Key"
                    className="form-input"
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleValidateGeminiKey}
                    disabled={!formData.geminiKey.trim() || isValidatingGemini}
                    style={{
                      whiteSpace: "nowrap",
                      padding: "0.5rem 0.75rem",
                      fontSize: "0.875rem",
                      minWidth: "80px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "0.25rem",
                    }}
                  >
                    {isValidatingGemini ? (
                      <>
                        <span>Checking...</span>
                      </>
                    ) : geminiKeyValid ? (
                      <>
                        <span>✓</span>
                        <span>Valid</span>
                      </>
                    ) : (
                      "Check Key"
                    )}
                  </button>
                </div>
              </div>

              <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                <label htmlFor="assemblyKey">Insert Assembly AI Key:</label>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <input
                    type="text"
                    id="assemblyKey"
                    value={formData.assemblyKey}
                    onChange={(e) =>
                      handleInputChange("assemblyKey", e.target.value)
                    }
                    placeholder="Enter your AssemblyAI API Key"
                    className="form-input"
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleValidateAssemblyKey}
                    disabled={
                      !formData.assemblyKey.trim() || isValidatingAssembly
                    }
                    style={{
                      whiteSpace: "nowrap",
                      padding: "0.5rem 0.75rem",
                      fontSize: "0.875rem",
                      minWidth: "80px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "0.25rem",
                    }}
                  >
                    {isValidatingAssembly ? (
                      <>
                        <span>Checking...</span>
                      </>
                    ) : assemblyKeyValid ? (
                      <>
                        <span>✓</span>
                        <span>Valid</span>
                      </>
                    ) : (
                      "Check Key"
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Process Files Button */}
            <div className="form-actions process-files-action">
              <button
                type="button"
                className="btn btn-success btn-full-width"
                onClick={handleProcessFilesClick}
              >
                Process Files
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Processing Modal */}
      {isProcessingModalOpen && (
        <div className="modal-overlay">
          <div
            className="modal-content"
            style={{ maxWidth: "700px", width: "90%" }}
          >
            <div className="modal-header">
              <h4>
                {modalStep === 1 && "File Processing / Review"}
                {modalStep === 2 && !isUsedPrompt && "Submit Feedback"}
              </h4>
              {/* Conditionally render or ensure close button is always disabled on step 2 */}
              {modalStep !== 2 && (
                <button
                  onClick={handleCloseModal}
                  className="btn-close-modal"
                  disabled={modalStep === 1 && !isLoadingFile && processedFile}
                >
                  &times;
                </button>
              )}
            </div>

            {/* Stepper Visual */}
            <div className="modal-stepper">
              <div className={`step-item ${modalStep === 1 ? "active" : ""}`}>
                1. Process & Review
              </div>
              <div className={`step-item ${modalStep === 2 ? "active" : ""}`}>
                2. Feedback
              </div>
            </div>

            {/* Modal Step 1: Processing or File View */}
            {modalStep === 1 && (
              <>
                <div className="modal-body">
                  {isLoadingFile ? (
                    <LoadingSpinner text="Processing your file, please wait..." />
                  ) : processedFile &&
                    processedFile.message &&
                    Array.isArray(processedFile.message.s3Links) &&
                    processedFile.message.s3Links.length > 0 ? (
                    <div className="file-view-area">
                      <h4
                        style={{
                          fontSize: "1.15rem",
                          fontWeight: 500,
                          marginBottom: 16,
                        }}
                      >
                        Generated Files
                      </h4>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fit, minmax(220px, 1fr))",
                          gap: "18px",
                        }}
                      >
                        {processedFile.message.s3Links.map((file, idx) => {
                          const ext = file.fileName
                            .split(".")
                            .pop()
                            .toLowerCase();
                          return (
                            <div
                              key={file.s3link + idx}
                              style={{
                                background: "#f9fafb",
                                border: "1px solid #e5e7eb",
                                borderRadius: 10,
                                padding: "1.2rem",
                                boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                minHeight: 120,
                              }}
                            >
                              <div
                                style={{
                                  fontWeight: 600,
                                  fontSize: "1.08em",
                                  marginBottom: 6,
                                }}
                              >
                                {file.fileName}
                              </div>
                              <div
                                style={{
                                  color: "#6b7280",
                                  fontSize: "0.97em",
                                  marginBottom: 12,
                                }}
                              >
                                Type: {ext}
                              </div>
                              <button
                                className="btn btn-outline"
                                style={{
                                  padding: "7px 18px",
                                  fontSize: "0.98em",
                                  borderRadius: 6,
                                }}
                                onClick={() =>
                                  window.open(file.s3link, "_blank")
                                }
                              >
                                View
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <p>Preparing for processing...</p>
                  )}
                </div>
                {!isLoadingFile && processedFile && (
                  <div className="modal-footer">
                    {isUsedPrompt ? (
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => {
                          setIsProcessingModalOpen(false);
                          setModalStep(0);
                          setProcessedFile(null);
                          window.location.reload();
                        }}
                      >
                        Done
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="btn btn-danger"
                          onClick={handleRejectFile}
                        >
                          Reject
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleAddFile}
                        >
                          Save
                        </button>
                      </>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Modal Step 2: Feedback Form */}
            {modalStep === 2 && !isUsedPrompt && (
              <>
                <div className="modal-body">
                  <div className="feedback-form">
                    <div className="form-group">
                      <label htmlFor="promptSearchName">
                        Name for this prompt search:
                      </label>
                      <input
                        type="text"
                        id="promptSearchName"
                        name="promptSearchName"
                        className="form-input"
                        value={feedbackData.promptSearchName}
                        onChange={handleFeedbackInputChange}
                        placeholder="e.g., Video Analysis Run 1"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="selectedTag">Tag:</label>
                      <select
                        id="selectedTag"
                        name="selectedTag"
                        className="form-select"
                        value={feedbackData.selectedTag}
                        onChange={handleFeedbackInputChange}
                      >
                        <option value="">Select a tag...</option>
                        {tagOptions.map((tag) => (
                          <option key={tag.value} value={tag.value}>
                            {tag.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <p>Rate the prompt (1-5):</p>
                    {["Accuracy", "Usefulness", "Relevancy", "Frequency"].map(
                      (crit) => (
                        <div className="form-group rating-group" key={crit}>
                          <label htmlFor={crit.toLowerCase()}>{crit}:</label>
                          <input
                            type="text"
                            id={crit.toLowerCase()}
                            name={crit.toLowerCase()}
                            className="form-input rating-input"
                            inputMode="decimal"
                            min="1"
                            max="5"
                            step="0.1"
                            placeholder="1.0 - 5.0"
                            value={
                              feedbackData[crit.toLowerCase()] === 0
                                ? ""
                                : feedbackData[crit.toLowerCase()]
                            }
                            onChange={handleFeedbackInputChange}
                          />
                          {feedbackFormErrors[crit.toLowerCase()] && (
                            <div className="inline-error-message">
                              {feedbackFormErrors[crit.toLowerCase()]}
                            </div>
                          )}
                        </div>
                      )
                    )}

                    {feedbackData.accuracy > 0 &&
                      feedbackData.accuracy <= 3 && (
                        <div className="form-group">
                          <label htmlFor="comment">
                            Your accuracy rating is {feedbackData.accuracy}.
                            Please provide comments:
                          </label>
                          <textarea
                            id="comment"
                            name="comment"
                            className="form-textarea"
                            rows="3"
                            value={feedbackData.comment}
                            onChange={handleFeedbackInputChange}
                            placeholder="Explain why the accuracy was low..."
                          ></textarea>
                        </div>
                      )}

                    {/* Checkbox to select Prompts field where the accuracy is poor - Placeholder */}
                    {feedbackData.accuracy > 0 &&
                      feedbackData.accuracy <= 3 && (
                        <div className="form-group">
                          <label>Which prompt fields had poor accuracy?</label>
                          {promptsToDisplay
                            .filter(
                              (p) =>
                                p.alwaysShow || (p.enabled && formData[p.key])
                            )
                            .map((prompt) => (
                              <div key={prompt.key} className="checkbox-label">
                                <input
                                  type="checkbox"
                                  id={`poor-${prompt.key}`}
                                  name="poorPromptFields"
                                  value={prompt.key}
                                  checked={
                                    !!feedbackData.poorPromptFields[prompt.key]
                                  }
                                  onChange={handleFeedbackInputChange}
                                  className="form-checkbox"
                                />
                                <label
                                  htmlFor={`poor-${prompt.key}`}
                                  className="checkbox-text"
                                >
                                  {prompt.label}
                                </label>
                              </div>
                            ))}
                        </div>
                      )}
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSubmitFeedback}
                  >
                    Submit Feedback
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {showFolderSelector && (
        <div
          className="modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "16px",
              width: "90%",
              maxWidth: "1200px",
              maxHeight: "90vh",
              overflow: "hidden",
            }}
          >
            <SelectLibraryComponet
              onSelect={handleSaveFolderSelect}
              onClose={() => setShowFolderSelector(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default MyPrompt;
